import { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Plus, Send, BarChart3, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, X } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

type SurveyStatus = 'draft' | 'active' | 'paused' | 'closed';
type SurveyType = 'enps' | 'engagement' | 'custom' | 'onboarding_checkin' | 'exit';

interface SurveyQuestion {
  id: string;
  text: string;
  type: string;
  required: boolean;
  options?: string[];
  order: number;
}

interface Survey {
  id: string;
  name: string;
  description?: string;
  survey_type: SurveyType;
  questions: SurveyQuestion[];
  status: SurveyStatus;
  frequency: string;
  audience: string;
  anonymous: boolean;
  response_count: number;
  response_rate: number;
  latest_nps_score?: number;
  last_sent_at?: string;
  next_send_date?: string;
  created_at: string;
}

interface Analytics {
  total_surveys: number;
  active_surveys: number;
  total_responses: number;
  overall_nps: number | null;
  avg_nps: number | null;
  monthly_trend: Array<{ month: string; nps: number | null; response_count: number }>;
  survey_breakdown: Array<{
    id: string;
    name: string;
    survey_type: string;
    status: string;
    response_count: number;
    nps_score: number | null;
    last_sent_at?: string;
  }>;
}

// ─── Helper Components ────────────────────────────────────────

function StatusBadge({ status }: { status: SurveyStatus }) {
  const styles: Record<SurveyStatus, string> = {
    active: 'bg-green-500/20 text-green-400',
    draft: 'bg-zinc-500/20 text-zinc-400',
    paused: 'bg-amber-500/20 text-amber-400',
    closed: 'bg-red-500/20 text-red-400',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[status]}`}>{status}</span>;
}

function NPSScore({ score }: { score: number | null }) {
  if (score === null) return <span className="text-muted-foreground">—</span>;
  const color = score >= 50 ? 'text-green-400' : score >= 20 ? 'text-amber-400' : score >= 0 ? 'text-orange-400' : 'text-red-400';
  const icon = score >= 20 ? <TrendingUp className="w-3.5 h-3.5" /> : score >= -10 ? <Minus className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />;
  return (
    <span className={`flex items-center gap-1 font-bold ${color}`}>
      {icon} {score > 0 ? '+' : ''}{score}
    </span>
  );
}

// ─── NPS Trend Chart ─────────────────────────────────────────

function NPSTrendChart({ trend }: { trend: Analytics['monthly_trend'] }) {
  const validPoints = trend.filter(p => p.nps !== null);
  if (validPoints.length < 2) return (
    <div className="h-24 flex items-center justify-center text-xs text-muted-foreground">
      Not enough data for trend chart
    </div>
  );

  const scores = validPoints.map(p => p.nps ?? 0);
  const min = Math.min(-100, ...scores);
  const max = Math.max(100, ...scores);
  const range = max - min || 1;
  const width = 300;
  const height = 80;

  const points = trend.map((p, i) => ({
    x: (i / (trend.length - 1)) * width,
    y: height - ((( p.nps ?? min) - min) / range) * height,
    month: p.month,
    nps: p.nps,
    count: p.response_count,
  }));

  const pathD = points
    .filter(p => p.nps !== null)
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`)
    .join(' ');

  // Zero line
  const zeroY = height - ((0 - min) / range) * height;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-20">
      {/* Zero line */}
      <line x1="0" y1={zeroY} x2={width} y2={zeroY} stroke="#3f3f46" strokeWidth="1" strokeDasharray="4,4" />
      {/* Trend line */}
      <path d={pathD} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Points */}
      {points.filter(p => p.nps !== null).map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill="#f59e0b" />
      ))}
    </svg>
  );
}

// ─── Create Survey Modal ──────────────────────────────────────

const SURVEY_TYPES: Array<{ value: SurveyType; label: string; description: string }> = [
  { value: 'enps', label: 'eNPS', description: 'Employee Net Promoter Score — 1 question, scale 1-10' },
  { value: 'engagement', label: 'Engagement', description: 'Multi-question engagement pulse survey' },
  { value: 'onboarding_checkin', label: 'Onboarding Check-in', description: '30/60/90 day new hire check-in' },
  { value: 'exit', label: 'Exit Survey', description: 'Departing employee feedback' },
  { value: 'custom', label: 'Custom', description: 'Build your own survey from scratch' },
];

const DEFAULT_QUESTIONS: Record<SurveyType, SurveyQuestion[]> = {
  enps: [
    { id: 'q1', text: 'On a scale of 0–10, how likely are you to recommend this company as a great place to work?', type: 'nps', required: true, order: 0 },
    { id: 'q2', text: 'What is the main reason for your score?', type: 'text', required: false, order: 1 },
  ],
  engagement: [
    { id: 'q1', text: 'I feel engaged and motivated in my work.', type: 'scale_1_10', required: true, order: 0 },
    { id: 'q2', text: 'I have the tools and resources I need to do my job well.', type: 'scale_1_10', required: true, order: 1 },
    { id: 'q3', text: 'My manager provides helpful feedback.', type: 'scale_1_10', required: true, order: 2 },
    { id: 'q4', text: 'What one thing would most improve your experience at work?', type: 'text', required: false, order: 3 },
  ],
  onboarding_checkin: [
    { id: 'q1', text: 'How well did your onboarding prepare you for your role?', type: 'scale_1_10', required: true, order: 0 },
    { id: 'q2', text: 'Do you feel welcomed by your team?', type: 'yes_no', required: true, order: 1 },
    { id: 'q3', text: 'What support would help you most right now?', type: 'text', required: false, order: 2 },
  ],
  exit: [
    { id: 'q1', text: 'What is your primary reason for leaving?', type: 'multiple_choice', required: true, order: 0, options: ['Career growth', 'Compensation', 'Management', 'Work-life balance', 'New opportunity', 'Other'] },
    { id: 'q2', text: 'Would you recommend this company to others?', type: 'yes_no', required: true, order: 1 },
    { id: 'q3', text: 'What could we have done differently to retain you?', type: 'text', required: false, order: 2 },
  ],
  custom: [],
};

interface CreateSurveyModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateSurveyModal({ onClose, onCreated }: CreateSurveyModalProps) {
  const [step, setStep] = useState<'type' | 'details'>('type');
  const [surveyType, setSurveyType] = useState<SurveyType>('enps');
  const [form, setForm] = useState({
    name: '',
    description: '',
    frequency: 'monthly',
    anonymous: true,
    next_send_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!form.name.trim()) { setError('Survey name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const questions = DEFAULT_QUESTIONS[surveyType].map(q => ({
        text: q.text,
        type: q.type,
        required: q.required,
        options: q.options,
        order: q.order,
      }));

      const res = await fetch('/api/people/surveys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          description: form.description || undefined,
          survey_type: surveyType,
          questions: questions.length > 0 ? questions : [{ text: 'Question 1', type: 'text', required: true, order: 0 }],
          frequency: form.frequency,
          anonymous: form.anonymous,
          next_send_date: form.next_send_date || undefined,
        }),
      });
      if (!res.ok) throw new Error('Failed to create survey');
      onCreated();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-xl p-6 w-full max-w-lg space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Create Pulse Survey</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {step === 'type' ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Choose a survey template:</p>
            {SURVEY_TYPES.map(t => (
              <button
                key={t.value}
                onClick={() => { setSurveyType(t.value); setStep('details'); if (!form.name) setForm(f => ({ ...f, name: t.label + ' Survey' })); }}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${surveyType === t.value ? 'border-amber-500/40 bg-amber-500/10' : 'border-border hover:border-border/80 hover:bg-surface-3'}`}
              >
                <p className="text-sm font-medium text-foreground">{t.label}</p>
                <p className="text-xs text-muted-foreground">{t.description}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Survey Name</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Description (optional)</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Frequency</label>
                <select
                  value={form.frequency}
                  onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                  className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
                >
                  <option value="once">One-time</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">First Send Date</label>
                <input
                  type="date"
                  value={form.next_send_date}
                  onChange={e => setForm(f => ({ ...f, next_send_date: e.target.value }))}
                  className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="anonymous"
                checked={form.anonymous}
                onChange={e => setForm(f => ({ ...f, anonymous: e.target.checked }))}
                className="rounded border-border"
              />
              <label htmlFor="anonymous" className="text-sm text-foreground">Anonymous responses</label>
            </div>
            <p className="text-xs text-muted-foreground">
              Template: {SURVEY_TYPES.find(t => t.value === surveyType)?.label} — {DEFAULT_QUESTIONS[surveyType].length} questions pre-loaded.
              You can customize questions after creation.
            </p>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          {step === 'details' && (
            <button onClick={() => setStep('type')} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Back</button>
          )}
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          {step === 'details' && (
            <button onClick={() => void save()} disabled={saving} className="px-4 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm disabled:opacity-50">
              {saving ? 'Creating...' : 'Create Survey'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Survey Card ─────────────────────────────────────────────

function SurveyCard({ survey, onSend }: { survey: Survey; onSend: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-foreground">{survey.name}</span>
            <StatusBadge status={survey.status} />
          </div>
          <p className="text-xs text-muted-foreground capitalize">
            {survey.survey_type.replace(/_/g, ' ')} &middot; {survey.frequency} &middot; {survey.anonymous ? 'anonymous' : 'identified'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {survey.status === 'draft' && (
            <button
              onClick={() => onSend(survey.id)}
              className="flex items-center gap-1 px-2.5 py-1 rounded bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 text-xs"
            >
              <Send className="w-3 h-3" /> Send
            </button>
          )}
          <div className="text-right">
            <NPSScore score={survey.latest_nps_score ?? null} />
            <p className="text-xs text-muted-foreground">{survey.response_count} responses</p>
          </div>
          <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground hover:text-foreground">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/50 space-y-3">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="glass rounded-lg p-2 text-center">
              <p className="text-muted-foreground mb-0.5">Questions</p>
              <p className="font-semibold text-foreground">{survey.questions.length}</p>
            </div>
            <div className="glass rounded-lg p-2 text-center">
              <p className="text-muted-foreground mb-0.5">Responses</p>
              <p className="font-semibold text-foreground">{survey.response_count}</p>
            </div>
            <div className="glass rounded-lg p-2 text-center">
              <p className="text-muted-foreground mb-0.5">eNPS</p>
              <p className="font-semibold">
                <NPSScore score={survey.latest_nps_score ?? null} />
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Questions</p>
            <div className="space-y-1.5">
              {survey.questions.map((q, i) => (
                <div key={q.id} className="flex items-start gap-2 text-xs">
                  <span className="text-muted-foreground">{i + 1}.</span>
                  <div className="flex-1">
                    <span className="text-foreground">{q.text}</span>
                    <span className="text-muted-foreground ml-2">({q.type.replace(/_/g, ' ')})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {survey.last_sent_at && (
            <p className="text-xs text-muted-foreground">
              Last sent: {new Date(survey.last_sent_at).toLocaleDateString()}
              {survey.next_send_date && ` · Next: ${survey.next_send_date}`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function PulseSurveysPage() {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState<'surveys' | 'analytics'>('surveys');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [surveyRes, analyticsRes] = await Promise.all([
        fetch('/api/people/surveys'),
        fetch('/api/people/surveys/analytics'),
      ]);
      if (surveyRes.ok) {
        const data = await surveyRes.json() as { surveys: Survey[] };
        setSurveys(data.surveys);
      }
      if (analyticsRes.ok) {
        const data = await analyticsRes.json() as Analytics;
        setAnalytics(data);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function sendSurvey(id: string) {
    try {
      const res = await fetch(`/api/people/surveys/${id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to send');
      await load();
    } catch {
      // silent
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">Pulse Surveys</h1>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm"
        >
          <Plus className="w-3.5 h-3.5" /> New Survey
        </button>
      </div>

      {/* KPI row */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Active Surveys</p>
            <p className="text-2xl font-bold text-foreground">{analytics.active_surveys}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Responses</p>
            <p className="text-2xl font-bold text-foreground">{analytics.total_responses}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Overall eNPS</p>
            <div className="text-2xl font-bold"><NPSScore score={analytics.overall_nps} /></div>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Avg NPS Score</p>
            <div className="text-2xl font-bold"><NPSScore score={analytics.avg_nps} /></div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1">
        {(['surveys', 'analytics'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${tab === t ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : tab === 'surveys' ? (
        <div className="space-y-3">
          {surveys.length === 0 ? (
            <div className="glass rounded-xl p-12 text-center">
              <ClipboardList className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
              <p className="text-foreground font-medium mb-1">No surveys yet</p>
              <p className="text-sm text-muted-foreground mb-4">Create your first pulse survey to measure employee sentiment.</p>
              <button onClick={() => setShowCreate(true)} className="px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm">
                Create First Survey
              </button>
            </div>
          ) : (
            surveys.map(s => (
              <SurveyCard
                key={s.id}
                survey={s}
                onSend={id => void sendSurvey(id)}
              />
            ))
          )}
        </div>
      ) : (
        /* Analytics tab */
        <div className="space-y-6">
          {analytics && (
            <>
              {/* Trend chart */}
              <div className="glass rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-4 h-4 text-amber-500" />
                  <h3 className="text-sm font-semibold text-foreground">6-Month eNPS Trend</h3>
                </div>
                <NPSTrendChart trend={analytics.monthly_trend} />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  {analytics.monthly_trend.map(p => (
                    <span key={p.month}>{p.month.slice(5)}</span>
                  ))}
                </div>
              </div>

              {/* Survey breakdown */}
              <div className="glass rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground">Survey Performance</h3>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-5 py-2.5 font-medium text-muted-foreground">Survey</th>
                      <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Type</th>
                      <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                      <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Responses</th>
                      <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">eNPS</th>
                      <th className="text-right px-5 py-2.5 font-medium text-muted-foreground">Last Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.survey_breakdown.map(s => (
                      <tr key={s.id} className="border-b border-border/50 hover:bg-surface-3/50">
                        <td className="px-5 py-2.5 text-sm text-foreground">{s.name}</td>
                        <td className="text-center px-4 py-2.5 text-muted-foreground capitalize">{s.survey_type.replace(/_/g, ' ')}</td>
                        <td className="text-center px-4 py-2.5">
                          <StatusBadge status={s.status as SurveyStatus} />
                        </td>
                        <td className="text-center px-4 py-2.5 text-foreground">{s.response_count}</td>
                        <td className="text-center px-4 py-2.5"><NPSScore score={s.nps_score} /></td>
                        <td className="text-right px-5 py-2.5 text-muted-foreground">
                          {s.last_sent_at ? new Date(s.last_sent_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {showCreate && (
        <CreateSurveyModal onClose={() => setShowCreate(false)} onCreated={() => { void load(); }} />
      )}
    </div>
  );
}
